import type { NotableOptionTradeRow } from '@/services/api';
import { optionTypeLabel, parseOccOptionSymbol } from '@/utils/parseOccOptionSymbol';

function formatPremiumUsd(premium: number): string {
  return `$${premium.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatExpiry(expiry: string | null | undefined): string {
  if (!expiry) return '—';
  const [y, m, d] = expiry.split('-').map(Number);
  if (!y || !m || !d) return expiry;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function NotableOptionTradesTooltipContent({
  trade,
  fullDate,
}: {
  trade: NotableOptionTradeRow;
  fullDate?: string;
}) {
  const parsed = parseOccOptionSymbol(trade.symbol);
  const optionType =
    trade.option_type === 'C' || trade.option_type === 'P' ? trade.option_type : parsed.optionType;
  const strike = trade.strike ?? parsed.strike;
  const expiry = trade.expiry ?? parsed.expiry;

  return (
    <div className="pr-5">
      <p className="text-[11px] font-semibold text-gray-900">
        {fullDate ?? trade.date}
        {trade.time ? ` · ${trade.time} ET` : ''}
      </p>
      <p className="mt-0.5 text-[10px] uppercase tracking-wide text-fuchsia-700">
        Notable option trade
      </p>
      <dl className="mt-2 space-y-1 text-[11px] text-gray-700">
        <div className="flex justify-between gap-3">
          <dt className="text-gray-400">Type</dt>
          <dd className="font-medium text-gray-800">{optionTypeLabel(optionType)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-gray-400">Premium</dt>
          <dd className="font-medium tabular-nums text-gray-900">
            {formatPremiumUsd(Number(trade.premium) || 0)}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-gray-400">Contracts</dt>
          <dd className="tabular-nums">{Number(trade.size).toLocaleString()}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-gray-400">Price</dt>
          <dd className="tabular-nums">${Number(trade.price).toFixed(2)}</dd>
        </div>
        {strike != null ? (
          <div className="flex justify-between gap-3">
            <dt className="text-gray-400">Strike</dt>
            <dd className="tabular-nums">${strike.toFixed(2)}</dd>
          </div>
        ) : null}
        <div className="flex justify-between gap-3">
          <dt className="text-gray-400">Expiry</dt>
          <dd>{formatExpiry(expiry)}</dd>
        </div>
        <div className="pt-1">
          <dt className="text-gray-400">Symbol</dt>
          <dd className="mt-0.5 break-all font-mono text-[10px] text-gray-600">{trade.symbol.trim()}</dd>
        </div>
      </dl>
    </div>
  );
}
